import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  Injector,
  Input,
  OnInit,
} from '@angular/core';

import { spreadArrayStore$, Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isEmpty, isEqual } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { isVideoOutput } from '../../../../../cross-team-shared/misc-utils/output-utils';
import { contentInjectorToken } from '../../../../../outputs/annotation/annotation-shared/abstract-annotation-output-component';
import { outputAnnotationInfoStoreToken } from '../../../../../outputs/annotation/annotation-shared/annotation-providers';
import { AbstractAnnotationStore2D } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store-2d';
import { AnnotationState2D } from '../../../../../outputs/annotation/annotation-shared/state/root/annotation-state-2d';
import { ImageAnnotationLayerState } from '../../../../../outputs/annotation/image-annotation/state/image-annotation-state';
import { Point2D } from '../../../../../team-apis/otterdam/answers/shape-typings';
import { CalibrationData } from '../../../../../team-apis/otterdam/calibration-data';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { isProjectionEnabled } from '../../../../shared/utils/projection-utils';
import { TransientLayerState } from '../state/layers/transient-layer-state';

export const DEFAULT_PIXEL_SIZE = 1;
export const ORTHOGRAPHIC_PIXEL_SIZE = 0.00001;

@Component({
  selector: 'h3-layers',
  templateUrl: './layers.component.html',
  styleUrls: ['./layers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LayersComponent extends DirectiveSuperclass implements OnInit {
  @Input() primaryOutputInjector?: Injector;
  @Input() calibrationData?: CalibrationData;
  @Input() isInsideZoomPanel = false;

  state!: AnnotationState2D;
  transientByLayerIndex$!: Array<Observable<TransientLayerState>>;
  outputName = this.outputInfoStore.state().output.name;
  layoutMode!: 'orthographic' | 'projection' | 'default';
  pixelSize!: number;

  layerStores$ = spreadArrayStore$<ImageAnnotationLayerState>(
    this.store('layers'),
  );
  movementDisabled$!: Observable<boolean>;

  isProjectionEnabled$: Observable<boolean>;

  lutData$ = this.taskWorkspaceStore('lutData').$;

  protected mainDisplaySize$ = this.store('mainDisplaySize').$.pipe(
    map((size) => size && ([size.width, size.height] as Point2D)),
  );
  protected sensorRendererSize$ = inject(outputAnnotationInfoStoreToken)(
    'sensorRendererState',
  ).$.pipe(
    map((state) => [state.width, state.height] as Point2D),
    distinctUntilChanged(isEqual),
  );

  constructor(
    @Inject(contentInjectorToken) public contentInjector: Injector,
    @Inject(outputInfoStoreToken) public outputInfoStore: Store<OutputInfo>,
    public store: AbstractAnnotationStore2D,
    private taskWorkspaceStore: TaskWorkspaceStore,
    injector: Injector,
  ) {
    super(injector);

    this.bindToInstance('state', this.store.$);
    this.bindToInstance(
      'transientByLayerIndex$',
      this.layerStores$.pipe(
        map((layerStores) =>
          layerStores.map((layerStore) => layerStore('transient').$),
        ),
      ),
    );
    this.isProjectionEnabled$ = this.getIsProjectionEnabled$();
  }

  ngOnInit() {
    this.pixelSize = DEFAULT_PIXEL_SIZE;
    if (this.primaryOutputInjector) {
      const { output } = this.outputInfoStore.state();
      assert(
        isVideoOutput(output),
        'Only the video output should have a primary output gangline',
      );

      if (output.meta.is_orthographic_projection) {
        this.layoutMode = 'orthographic';
        this.pixelSize = ORTHOGRAPHIC_PIXEL_SIZE;
      } else {
        this.layoutMode = 'projection';
      }
    } else {
      this.layoutMode = 'default';
    }

    this.movementDisabled$ = this.store('focus').$.pipe(
      map((focus) => this.isInsideZoomPanel !== (focus === 'zoomPanel')),
    );
  }

  getIsProjectionEnabled$() {
    return combineLatest([
      this.getInput$('calibrationData'),
      this.taskWorkspaceStore('lutData').$.pipe(startWith(undefined)),
    ]).pipe(
      map(
        ([calibrationData, lutData]) =>
          !isEmpty(calibrationData) &&
          isProjectionEnabled(
            this.taskWorkspaceStore.state(),
            this.outputName,
            lutData,
          ),
      ),
    );
  }
}
